/**
 * Incidents IT : règles métier pures, partagées par l'API et le front (statuts, cycle de vie, priorités, SLA).
 * Référence fonctionnelle : ReFresh (packages/shared incidents.ts, incident-workflow.ts, sla.ts ;
 * apps/api sla-calculation.ts), transposée dans les conventions AGORA.
 *
 * Libellés : clés `it.incidents.statuts.<statut>`, `it.incidents.priorites.<priorité>`, `it.incidents.sla.<état>`.
 * Chaque règle renvoie à sa ligne du registre des décisions (docs/it/incidents/DECISIONS-INCIDENTS.md) ou à son Issue ;
 * une valeur encore à valider porte la mention PROVISOIRE avec son Issue.
 */
import type { EquipeIt, NiveauEquipe } from '../api-types';
import type { Action } from '../droits';

/**
 * Ressource de droits (Verified Permissions) et nœud du catalogue des incidents. Seul endroit où figure cet identifiant.
 *
 * Sous-module `incidents` du module `it.demandes` (décision du 22/10/2026) ; le nœud du catalogue est maintenu par
 * Vincent Barbier dans `catalogue.ts`.
 */
export const RESSOURCE_INCIDENTS = 'it.demandes.incidents';

/** Statuts d'un incident, dans l'ordre du cycle de vie. */
export const STATUTS_INCIDENT = ['nouveau', 'qualifie', 'affecte', 'en-traitement', 'en-attente', 'resolu', 'cloture'] as const;
export type StatutIncident = (typeof STATUTS_INCIDENT)[number];

/**
 * Transitions de statut, par statut de départ. Source unique : toute action qui change le statut d'un incident passe
 * par cette table (refus = 409). Une réaffectation en traitement ou en attente ramène l'incident à `affecte` ; un
 * incident résolu ou clôturé repasse en traitement (réouverture) avant de pouvoir être réaffecté.
 *
 * Statuts et transitions repris de ReFresh, validés par Benoit (D-IT-020, #195) ; s'y ajouteront le statut « Annulé »
 * (#170) et la pause du SLA (#178).
 */
export const TRANSITIONS_INCIDENT: { readonly [S in StatutIncident]: readonly StatutIncident[] } = {
  nouveau: ['qualifie'],
  qualifie: ['affecte'],
  affecte: ['en-traitement'],
  'en-traitement': ['en-attente', 'resolu', 'affecte'],
  'en-attente': ['en-traitement', 'affecte'],
  resolu: ['cloture', 'en-traitement'],
  cloture: ['en-traitement'],
};

export function transitionAutorisee(de: StatutIncident, vers: StatutIncident): boolean {
  return TRANSITIONS_INCIDENT[de].includes(vers);
}

export interface DefinitionActionIncident {
  /** Statuts depuis lesquels l'action est possible. */
  depuis: readonly StatutIncident[];
  /** Statut après l'action ; null : l'action ne change pas le statut. */
  vers: StatutIncident | null;
  /** Droit exigé sur le sous-module : déclaration de la route et de l'outil, contrôle du service, boutons du front. */
  droit: Action;
}

/**
 * Actions sur le cycle de vie. Chaque passage `depuis → vers` d'un statut à un autre figure dans TRANSITIONS_INCIDENT
 * (vérifié par test) ; seule la réaffectation d'un incident `affecte` garde son statut, et la requalification ne change
 * jamais le statut. Droits : `modifier` pour le traitement, `valider` pour la résolution et la clôture, `creer` pour la
 * réouverture (le demandeur n'a que lire et creer ; la règle d'acteur la lui réserve). Les règles d'acteur (refusActeur)
 * et le délai de réouverture (reouvertureOuverte) sont contrôlés par les services de l'API.
 *
 * Actions reprises de ReFresh, validées avec le cycle (D-IT-020, #195) ; clôture par tout agent ayant `valider`, jamais
 * par le demandeur (D-IT-022, #181).
 */
export const ACTIONS_INCIDENT = {
  /** Qualification : l'agent confirme ou corrige la catégorie et la priorité déclarées. */
  qualifier: { depuis: ['nouveau'], vers: 'qualifie', droit: 'modifier' },
  /** Requalification de la catégorie ou de la priorité, tant que l'incident n'est ni résolu ni clôturé. */
  requalifier: { depuis: ['nouveau', 'qualifie', 'affecte', 'en-traitement', 'en-attente'], vers: null, droit: 'modifier' },
  /** Affectation (depuis `qualifie`) ou réaffectation, tant que l'incident n'est ni résolu ni clôturé. */
  affecter: { depuis: ['qualifie', 'affecte', 'en-traitement', 'en-attente'], vers: 'affecte', droit: 'modifier' },
  /**
   * Prise en charge par l'agent affecté ou un membre de l'équipe affectée, qui devient l'agent affecté.
   * La première prise en charge vaut première réponse (voir enregistrerPremiereReponse).
   */
  prendreEnCharge: { depuis: ['affecte'], vers: 'en-traitement', droit: 'modifier' },
  /** Mise en attente par l'agent affecté. */
  mettreEnAttente: { depuis: ['en-traitement'], vers: 'en-attente', droit: 'modifier' },
  /** Reprise par l'agent affecté ; aussi automatique quand le demandeur répond (repriseParReponse, commentaires.ts). */
  reprendre: { depuis: ['en-attente'], vers: 'en-traitement', droit: 'modifier' },
  /** Résolution par l'agent affecté, solution obligatoire (visible du demandeur) : fige l'étape « résolution » du SLA. */
  resoudre: { depuis: ['en-traitement'], vers: 'resolu', droit: 'valider' },
  /** Clôture d'un incident résolu par tout autre agent ayant `valider` (D-IT-022, #181), jamais par son demandeur (I20). */
  cloturer: { depuis: ['resolu'], vers: 'cloture', droit: 'valider' },
  /**
   * Réouverture par le demandeur, sans nouvel incident : depuis `resolu` (refus de la solution) ou depuis `cloture`
   * dans le délai de réouverture ; retour en traitement chez l'agent affecté, compteur de réouvertures incrémenté.
   * Décision du 27/10/2026 : réouverture depuis `resolu` conservée (le demandeur peut refuser la solution).
   */
  rouvrir: { depuis: ['resolu', 'cloture'], vers: 'en-traitement', droit: 'creer' },
  /**
   * Escalade manuelle vers une équipe d'un niveau technique supérieur, saut de niveau compris (N1 → N2 ou N3, N2 → N3),
   * avec un motif (D-IT-123, D-IT-131) : réaffectation à cette équipe, statut `affecte`. Depuis un incident affecté à
   * une équipe (affecte, en traitement, en attente), par l'agent affecté ou un membre actif de l'équipe affectée ; depuis
   * un incident qualifié pas encore affecté, par tout agent, vers N2 ou N3 (D-IT-146, #188, #252). Jamais depuis
   * « nouveau » (qualifié d'abord), « résolu » ni « clôturé » (le demandeur rouvre d'abord : #252).
   */
  escalader: { depuis: ['qualifie', 'affecte', 'en-traitement', 'en-attente'], vers: 'affecte', droit: 'modifier' },
} as const satisfies Record<string, DefinitionActionIncident>;

export type ActionIncident = keyof typeof ACTIONS_INCIDENT;

export function actionPossible(action: ActionIncident, statut: StatutIncident): boolean {
  const depuis: readonly StatutIncident[] = ACTIONS_INCIDENT[action].depuis;
  return depuis.includes(statut);
}

/** Priorités, de la plus urgente (1) à la moins urgente (4) : `level` du composant PriorityTag. Chacune a son SLA. */
export const PRIORITES_INCIDENT = [1, 2, 3, 4] as const;
export type PrioriteIncident = (typeof PRIORITES_INCIDENT)[number];

/**
 * Priorité d'un incident déclaré, avant qualification par un agent (qui recalcule alors les échéances SLA).
 *
 * Valeur reprise de ReFresh, validée par Benoit (D-IT-045, #209).
 */
export const PRIORITE_INCIDENT_DEFAUT: PrioriteIncident = 3;

/** Délais du SLA d'une priorité, en minutes calendaires. */
export interface DelaisSla {
  premiereReponseMin: number;
  resolutionMin: number;
}

export interface EcheancesSla {
  premiereReponse: Date;
  resolution: Date;
}

/**
 * Plafond d'un délai du SLA, en minutes (un an), comme ReFresh ; aussi contrôlé en base (it.sla).
 *
 * Valeur validée par Benoit (D-IT-046, #209).
 */
export const SLA_DELAI_MAX_MIN = 525_600;

const MS_PAR_MINUTE = 60_000;

/**
 * Échéances d'un incident depuis sa date de création, en minutes calendaires (nuits, week-ends et changements d'heure
 * compris). Aussi après un changement de priorité : on repart toujours de la création. Seul point de calcul des
 * échéances : les heures ouvrées (plus tard) remplaceront cette addition.
 */
export function calculerEcheances(delais: DelaisSla, creeLe: Date): EcheancesSla {
  return {
    premiereReponse: new Date(creeLe.getTime() + delais.premiereReponseMin * MS_PAR_MINUTE),
    resolution: new Date(creeLe.getTime() + delais.resolutionMin * MS_PAR_MINUTE),
  };
}

/**
 * État du SLA d'une étape (première réponse ou résolution), calculé à la lecture, jamais stocké : tant que l'étape
 * n'est pas atteinte, il dépend de l'heure ; une fois atteinte, il est figé.
 */
export const ETATS_SLA = ['ok', 'en-risque', 'depasse'] as const;
export type EtatSla = (typeof ETATS_SLA)[number];

/**
 * Part du délai consommée (en %) à partir de laquelle une étape non atteinte passe `en-risque`.
 *
 * 75 %, validé par Benoit (D-IT-044) ; paramétrable et commun au groupe : décidé, à appliquer (#215, #216).
 */
export const SEUIL_SLA_EN_RISQUE_POURCENT = 75;

/** Une étape du SLA : départ, échéance (null pour un incident sans échéance) et date à laquelle elle a été atteinte. */
export interface EtapeSla {
  debut: Date;
  echeance: Date | null;
  atteinteLe: Date | null;
}

/**
 * État d'une étape à l'instant `maintenant` :
 * - sans échéance : null ;
 * - étape atteinte : figé, `ok` si elle l'a été au plus tard à l'échéance, `depasse` sinon ;
 * - échéance passée : `depasse` (pile à l'échéance, l'étape est encore `en-risque`) ;
 * - au moins SEUIL_SLA_EN_RISQUE_POURCENT % du délai consommé : `en-risque` ;
 * - sinon : `ok`.
 * Comparaison en millisecondes entières, sans division : les bornes sont exactes.
 */
export function etatEtapeSla(etape: EtapeSla, maintenant: Date): EtatSla | null {
  if (etape.echeance === null) return null;
  const echeance = etape.echeance.getTime();
  if (etape.atteinteLe !== null) return etape.atteinteLe.getTime() <= echeance ? 'ok' : 'depasse';
  const instant = maintenant.getTime();
  if (instant > echeance) return 'depasse';
  const debut = etape.debut.getTime();
  return (instant - debut) * 100 >= (echeance - debut) * SEUIL_SLA_EN_RISQUE_POURCENT ? 'en-risque' : 'ok';
}

/** Dates d'un incident utiles au calcul de l'état de son SLA. */
export interface DatesSlaIncident {
  creeLe: Date;
  echeancePremiereReponse: Date | null;
  echeanceResolution: Date | null;
  premiereReponseLe: Date | null;
  resoluLe: Date | null;
}

export interface EtatSlaIncident {
  premiereReponse: EtatSla | null;
  resolution: EtatSla | null;
}

/**
 * État du SLA d'un incident, par étape. Les deux étapes partent de la création de l'incident, y compris après un
 * changement de priorité. Une résolution sans réponse préalable vaut première réponse (la solution est visible du
 * demandeur).
 *
 * Pas encore de pause du SLA pendant la mise en attente (comme ReFresh). Décidée (D-IT-047) : seul le motif « attente du
 * demandeur » suspend le SLA, première réponse comprise, et les échéances reculent de la durée des pauses ; à appliquer
 * (#178) au point d'accroche `planReprise` de l'API (cycle-regles.ts). Décision du 27/10/2026 : après une réouverture,
 * le SLA garde l'échéance d'origine.
 */
export function etatSlaIncident(incident: DatesSlaIncident, maintenant: Date): EtatSlaIncident {
  return {
    premiereReponse: etatEtapeSla(
      {
        debut: incident.creeLe,
        echeance: incident.echeancePremiereReponse,
        atteinteLe: incident.premiereReponseLe ?? incident.resoluLe,
      },
      maintenant,
    ),
    resolution: etatEtapeSla(
      { debut: incident.creeLe, echeance: incident.echeanceResolution, atteinteLe: incident.resoluLe },
      maintenant,
    ),
  };
}

/**
 * Dates du SLA telles que les renvoie l'API (ISO 8601 UTC). `resoluLe` est absent d'une ligne de liste : son statut
 * suffit alors (un incident résolu ou clôturé n'a plus d'étape en cours).
 */
export interface DatesSlaApi {
  creeLe: string;
  echeancePremiereReponse: string | null;
  echeanceResolution: string | null;
  premiereReponseLe: string | null;
  resoluLe?: string | null;
}

const enDate = (v: string | null | undefined) => (v ? new Date(v) : null);

/**
 * État du SLA à l'instant `maintenant`, à partir des dates renvoyées par l'API. Les écrans l'utilisent avec l'heure
 * courante, rafraîchie régulièrement : le champ `sla` de la réponse est calculé au chargement et vieillit.
 */
export function etatSlaAMaintenant(incident: DatesSlaApi, maintenant: Date): EtatSlaIncident {
  return etatSlaIncident(
    {
      creeLe: new Date(incident.creeLe),
      echeancePremiereReponse: enDate(incident.echeancePremiereReponse),
      echeanceResolution: enDate(incident.echeanceResolution),
      premiereReponseLe: enDate(incident.premiereReponseLe),
      resoluLe: enDate(incident.resoluLe),
    },
    maintenant,
  );
}

export type NomEtapeSla = keyof EtatSlaIncident;

/**
 * Prochaine étape non atteinte du SLA à l'instant `maintenant` (« SLA restant » de la liste) : la première réponse
 * tant qu'elle manque, puis la résolution tant que l'incident n'est ni résolu ni clôturé ; null ensuite ou sans
 * échéance. L'état est celui de cette étape à cet instant (jamais figé au chargement).
 */
export function prochaineEtapeSla(
  incident: DatesSlaApi & { statut: StatutIncident },
  maintenant: Date,
): { etape: NomEtapeSla; echeance: string; etat: EtatSla | null } | null {
  if (incident.statut === 'resolu' || incident.statut === 'cloture') return null;
  const debut = new Date(incident.creeLe);
  const etape = (nom: NomEtapeSla, echeance: string) => ({
    etape: nom,
    echeance,
    etat: etatEtapeSla({ debut, echeance: new Date(echeance), atteinteLe: null }, maintenant),
  });
  if (!incident.premiereReponseLe && incident.echeancePremiereReponse) return etape('premiereReponse', incident.echeancePremiereReponse);
  if (incident.echeanceResolution) return etape('resolution', incident.echeanceResolution);
  return null;
}

/**
 * Première réponse au demandeur : la première prise en charge, ou le premier commentaire public d'un agent
 * (it/commentaires.ts). Elle fige l'étape « première réponse » du SLA et n'est jamais réécrite. `nouvelle` indique au
 * service qu'il vient de l'enregistrer (à auditer).
 */
export function enregistrerPremiereReponse(
  premiereReponseLe: Date | null,
  maintenant: Date,
): { premiereReponseLe: Date; nouvelle: boolean } {
  return premiereReponseLe === null
    ? { premiereReponseLe: maintenant, nouvelle: true }
    : { premiereReponseLe, nouvelle: false };
}

/**
 * Refus d'acteur d'une action (403) : au-delà du droit de l'action et de la visibilité, qui peut agir sur un incident.
 * - `demandeur` : séparation des tâches (D-IT-124, ISO 27001) : un agent ne traite jamais un incident dont il est le
 *   demandeur, quel que soit le déclarant. Il ne le qualifie, ne le requalifie, ne l'affecte (ni à une équipe ni à un
 *   collègue : I19), ne le prend en charge, ne le met en attente, ne le reprend, ne le résout ni ne le clôture (I20),
 *   même membre de l'équipe ou (incident antérieur à la règle) agent affecté. Contrôlé avant `non-affecte`. L'agent qui
 *   déclare pour un tiers n'est pas concerné ;
 * - `non-affecte` : seul l'agent affecté met en attente, reprend ou résout ; prendre en charge est ouvert à l'agent
 *   affecté et aux membres actifs de l'équipe affectée ;
 * - `non-demandeur` : seul le demandeur rouvre son incident, même s'il est agent.
 * - escalader : comme prendre en charge (`demandeur`, puis agent affecté ou membre actif de l'équipe affectée ;
 *   D-IT-131) ; un incident sans équipe (qualifié, pas encore affecté) s'escalade par tout agent sauf le demandeur
 *   (D-IT-146, #188). Le DSI, aussi cité par Benoit, n'est pas encore connu d'AGORA (D-IT-148, #194).
 * L'affectation (le demandeur n'est jamais choisi comme agent : agentAffectable) et la clôture (D-IT-022, #181)
 * sont ouvertes à tout autre agent. E-mails en minuscules (forme unique du socle). Partagé avec le front (boutons
 * d'action affichés ou non, explication de la séparation des tâches).
 */
export type RefusActeurIncident = 'demandeur' | 'non-affecte' | 'non-demandeur';

export function refusActeur(
  action: ActionIncident,
  incident: { demandeur: string; affecteA: string | null; equipeId: string | null },
  moi: string,
  estMembreEquipe: boolean,
): RefusActeurIncident | null {
  switch (action) {
    case 'qualifier':
    case 'requalifier':
    case 'affecter':
    case 'cloturer':
      return incident.demandeur === moi ? 'demandeur' : null;
    case 'escalader':
      if (incident.demandeur === moi) return 'demandeur';
      if (incident.equipeId === null) return null;
      return incident.affecteA === moi || estMembreEquipe ? null : 'non-affecte';
    case 'prendreEnCharge':
      if (incident.demandeur === moi) return 'demandeur';
      return incident.affecteA === moi || estMembreEquipe ? null : 'non-affecte';
    case 'mettreEnAttente':
    case 'reprendre':
    case 'resoudre':
      if (incident.demandeur === moi) return 'demandeur';
      return incident.affecteA === moi ? null : 'non-affecte';
    case 'rouvrir':
      return incident.demandeur === moi ? null : 'non-demandeur';
  }
}

/**
 * Agent qui peut devenir l'agent affecté de l'incident (D-IT-124) : jamais son demandeur. `null` (équipe seule) l'est
 * toujours. Même règle que la contrainte incident_agent_non_demandeur ; partagée par l'affectation et l'escalade (API,
 * maquette, liste des agents proposés) et, demain, l'affectation automatique. Une équipe dont aucun membre actif n'est
 * affectable ne reçoit ni affectation ni escalade (D-IT-147, #248 : 400 sur `equipeId`).
 */
export function agentAffectable(incident: { demandeur: string }, agent: string | null): boolean {
  return agent !== incident.demandeur;
}

/**
 * Qui suit l'incident du côté de la demande, à prévenir de chaque changement de statut et de chaque réponse visible d'un
 * agent (F-13, #244 ; F-02, #234) : le demandeur, puis le déclarant pour un tiers s'il est une autre personne, prévenu
 * comme le demandeur. L'auteur de l'action n'est jamais notifié : le socle le retire (notifier). E-mails en minuscules.
 */
export function destinatairesDemandeur(incident: { demandeur: string; declarant: string }): string[] {
  return incident.declarant !== incident.demandeur ? [incident.demandeur, incident.declarant] : [incident.demandeur];
}

/** Niveaux techniques, du premier au dernier (D-IT-123 : N1 → N2 → N3). */
const ORDRE_NIVEAUX: readonly NiveauEquipe[] = ['n1', 'n2', 'n3'];

/**
 * Niveaux techniques supérieurs à celui d'une équipe, du plus proche au plus éloigné ; aucun au dernier niveau. Saut de
 * niveau autorisé (D-IT-131 : N1 → N3 direct). La demande de validation au DSI n'est pas une escalade vers une équipe
 * (D-IT-130) : elle n'est pas développée.
 */
export function niveauxSuperieurs(niveau: NiveauEquipe): NiveauEquipe[] {
  return ORDRE_NIVEAUX.slice(ORDRE_NIVEAUX.indexOf(niveau) + 1);
}

/**
 * Équipes vers lesquelles un incident affecté à l'équipe `equipeId` peut être escaladé : les équipes actives d'un niveau
 * strictement supérieur, du niveau le plus proche au plus éloigné (ordre reçu gardé dans un niveau). Incident sans équipe
 * (qualifié, pas encore affecté) : les équipes actives de N2 et de N3 (D-IT-146, #252). Aucune pour une équipe inconnue
 * ou au dernier niveau. Partagé par l'API, la maquette et la fiche (bouton « Escalader » proposé seulement s'il y a une
 * cible).
 */
export function equipesEscalade<E extends Pick<EquipeIt, 'id' | 'niveau' | 'actif'>>(equipeId: string | null, equipes: readonly E[]): E[] {
  const niveau = equipeId === null ? ORDRE_NIVEAUX[0] : equipes.find((e) => e.id === equipeId)?.niveau;
  return niveau ? niveauxSuperieurs(niveau).flatMap((n) => equipes.filter((e) => e.actif && e.niveau === n)) : [];
}

/**
 * Catégorie encore sélectionnable dans un formulaire (déclaration, qualification) : elle-même ET sa racine actives,
 * même règle que l'API (verifierCategorie : refus 400 sinon). Renvoie l'identifiant, ou '' si la catégorie est
 * inconnue ou devenue inactive : le formulaire part alors d'un choix vide au lieu de renvoyer l'ancienne valeur.
 */
export function categorieSelectionnable(
  categories: readonly { id: string; parentId: string | null; actif: boolean }[],
  id: string | null,
): string {
  const c = categories.find((x) => x.id === id);
  if (!c?.actif) return '';
  const parent = c.parentId ? categories.find((x) => x.id === c.parentId) : undefined;
  return c.parentId && !parent?.actif ? '' : c.id;
}

const MS_PAR_JOUR = 24 * 60 * MS_PAR_MINUTE;

/**
 * Fin du délai de réouverture d'un incident clôturé (la réouverture reste possible pile à cette date). Le délai, en
 * jours calendaires comptés depuis la clôture (ReFresh US #97), est un paramètre du module commun au groupe
 * (PARAMETRES_IT.reouvertureJours, it.parametre, #216) : l'API le lit en base, le front dans les paramètres du module.
 */
export function finDelaiReouverture(clotureLe: Date, delaiJours: number): Date {
  return new Date(clotureLe.getTime() + delaiJours * MS_PAR_JOUR);
}

/**
 * Réouverture encore ouverte à l'instant `maintenant` : toujours au statut `resolu` (pas encore clôturé), au statut
 * `cloture` jusqu'à la fin du délai en vigueur (`delaiJours`), jamais sinon. Statut de départ et règle d'acteur :
 * actionPossible, refusActeur. Partagé avec le front (bouton « Rouvrir » affiché ou non) ; l'API reste seule juge (409
 * hors délai).
 */
export function reouvertureOuverte(
  incident: { statut: StatutIncident; clotureLe: string | Date | null },
  maintenant: Date,
  delaiJours: number,
): boolean {
  if (incident.statut === 'resolu') return true;
  if (incident.statut !== 'cloture' || incident.clotureLe === null) return false;
  return maintenant.getTime() <= finDelaiReouverture(new Date(incident.clotureLe), delaiJours).getTime();
}
